import { useCallback, useState } from "react";

export interface CursorPagination {
  /** Cursor for the page to request. `undefined` is the first page. */
  readonly cursor: string | undefined;
  /** 1-based position of the page on screen. */
  readonly page: number;
  /**
   * Total pages, or `undefined` when the endpoint has not said. Derived from a
   * `totalCount` the caller passes in — see the note on stickiness below.
   */
  readonly pageCount: number | undefined;
  /** First-page total retained for truthful range copy on later pages. */
  readonly totalCount: number | undefined;
  /** False on the first page — nothing to go back to. */
  readonly canGoBack: boolean;
  /** Advance using the cursor the last page handed out. */
  readonly goNext: (nextCursor: string) => void;
  /** Return to the page before this one. */
  readonly goBack: () => void;
  /**
   * Hand back the `totalCount` of the response once it is known. Safe — and
   * intended — to call DURING render, right after the query hook.
   *
   * It exists because the dependency runs both ways: the query needs this
   * hook's `cursor`, so it cannot be called first, yet the total only arrives
   * with the response. Rather than have every page invent its own way out —
   * parking it in local state, or reading the first page back out of the query
   * cache — the latch lives here once. Calls with `undefined`, or with the
   * value already held, do nothing.
   */
  readonly reportTotal: (totalCount: number | undefined) => void;
}

interface PageState {
  readonly key: string;
  readonly cursor: string | undefined;
  readonly trail: readonly (string | undefined)[];
  /** Held across pages — see `useCursorPagination`. */
  readonly total: number | undefined;
}

const FIRST_PAGE = { cursor: undefined, trail: [], total: undefined } as const;

export interface UseCursorPaginationOptions {
  /** Rows per page, used only to turn a row total into a page total. */
  readonly pageSize: number;
}

/**
 * Cursor paging state for a filtered list.
 *
 * `resetKey` is whatever identifies the result set — the serialised filters and
 * page size. When it changes, this returns the FIRST page in the same render.
 *
 * That timing is the whole point, and the reason this is not four `useState`s
 * and an effect at each call site. Resetting the cursor from `useEffect` leaves
 * one render where the new filters are paired with the OLD cursor, and a list
 * that builds its query during render will fetch that combination — a cursor
 * into a result set that no longer exists. The reset has to be DERIVED, not
 * scheduled, so the invalid pairing is unrepresentable rather than merely
 * short-lived.
 *
 * The server only ever hands out a forward cursor, so going back replays a
 * locally kept trail; that is also why `canGoBack` is state rather than
 * something a caller can infer from the current cursor alone. For the same
 * reason `page` counts the trail rather than being read off the cursor: a
 * keyset cursor is a position, not an index.
 */
export function useCursorPagination(
  resetKey: string,
  options: UseCursorPaginationOptions,
): CursorPagination {
  const { pageSize } = options;
  const [state, setState] = useState<PageState>({
    key: resetKey,
    ...FIRST_PAGE,
  });

  /* Adjusting state DURING render (React's documented pattern for "reset when a
     prop changes"), not in an effect. React re-renders this component
     immediately without committing the discarded output, so no child ever sees
     the mismatched pair — where an effect would let one render escape with the
     new key and the old cursor.

     Committing it also matters beyond that render: deriving alone would leave
     the stale entry in state, so returning to an earlier key would resurrect a
     cursor issued against a result set that has since been re-queried. */
  if (state.key !== resetKey) {
    setState({ key: resetKey, ...FIRST_PAGE });
  }

  // This render still holds the pre-adjustment value, so read through the key.
  const active: PageState =
    state.key === resetKey ? state : { key: resetKey, ...FIRST_PAGE };
  const total = active.total;

  /* Endpoints send the total on the FIRST page only, so this HOLDS the last
     value seen for the current key — otherwise "of 12" would vanish the moment
     page two arrived without a count. A different value for the same key is a
     real change (a row created or deleted), so it replaces rather than pins. */
  const reportTotal = (next: number | undefined): void => {
    if (next === undefined) return;
    if (state.key === resetKey && state.total === next) return;
    setState((current) =>
      current.key === resetKey && current.total === next
        ? current
        : { ...current, key: resetKey, total: next },
    );
  };

  const goNext = useCallback(
    (nextCursor: string) => {
      setState((current) => ({
        key: resetKey,
        cursor: nextCursor,
        trail: [...current.trail, current.cursor],
        total: current.total,
      }));
    },
    [resetKey],
  );

  const goBack = useCallback(() => {
    setState((current) => {
      const trail = [...current.trail];
      const previous = trail.pop();
      return { key: resetKey, cursor: previous, trail, total: current.total };
    });
  }, [resetKey]);

  return {
    cursor: active.cursor,
    page: active.trail.length + 1,
    // A page count only means anything with a row total AND a positive size.
    pageCount:
      total === undefined || pageSize <= 0
        ? undefined
        : Math.max(1, Math.ceil(total / pageSize)),
    totalCount: total,
    canGoBack: active.trail.length > 0,
    goNext,
    goBack,
    reportTotal,
  };
}
